import React, { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bot, ExternalLink } from 'lucide-react';
import { cn } from 'lib/utils';
import { CONNECTOR_BADGE_CLASS } from '../utils/connector-link-badges';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { buildChatLandingPath } from 'utils/chat-utilities';
import type { PlatformResourceChangedData } from 'models/websockets.types';

interface TryAgentBadgeProps {
  change: PlatformResourceChangedData;
}

export const TryAgentBadge: React.FC<TryAgentBadgeProps> = ({ change }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = useCommonStore((state) => state.user?.id);
  const isIncognitoChat = searchParams.get('incognito-chat') === 'true';

  const handleClick = useCallback(() => {
    if (!change.resourceId) return;
    if (userId) {
      useAgentSelectionStore.getState().setSelectedAgentId(userId, change.resourceId);
    }
    useChatStore.getState().setActiveConversationId(null);
    useChatStore.getState().setReplyMessage(null);
    navigate(
      buildChatLandingPath({
        agentId: change.resourceId,
        isIncognito: isIncognitoChat,
      })
    );
  }, [change.resourceId, userId, isIncognitoChat, navigate]);

  if (!change.resourceId) return null;

  const label = change.name ? `Try ${change.name}` : 'Try this agent';
  const landingPath = buildChatLandingPath({
    agentId: change.resourceId,
    isIncognito: isIncognitoChat,
  });

  return (
    <a
      href={landingPath}
      onClick={(e) => {
        e.preventDefault();
        handleClick();
      }}
      title={label}
      className={cn(
        'group mr-1 my-0.5 inline-flex items-center gap-1.5 align-middle rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer shadow-xs transition-all duration-150 select-none no-underline active:scale-95 border border-gray-300 dark:border-gray-500',
        CONNECTOR_BADGE_CLASS
      )}
    >
      <Bot className="h-3.5 w-3.5 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity" />
      <span>{label}</span>
      <ExternalLink className="h-3 w-3 shrink-0 opacity-70 transition-opacity group-hover:opacity-100" />
    </a>
  );
};

interface TryAgentBadgesListProps {
  changes?: PlatformResourceChangedData[];
}

export const TryAgentBadgesList: React.FC<TryAgentBadgesListProps> = ({ changes }) => {
  if (!changes || changes.length === 0) return null;

  const createdAgents = changes.filter(
    (res, idx, arr) =>
      (res.kind === 'agents' || !res.kind) &&
      res.action !== 'deleted' &&
      Boolean(res.resourceId) &&
      arr.findIndex((r) => r.resourceId === res.resourceId) === idx
  );

  if (createdAgents.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      {createdAgents.map((res) => (
        <TryAgentBadge key={res.resourceId} change={res} />
      ))}
    </div>
  );
};
